import * as pdfjsLib from "pdfjs-dist";
import { TextLayerBuilder } from "pdfjs-dist/web/pdf_viewer.mjs";
import type {
  PDFDocumentProxy,
  PDFPageProxy,
  PageViewport,
  RenderTask,
} from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.mjs?url";
import {
  DEFAULT_DOCUMENT_ID,
  normalizeShowRegionCommand,
} from "./commandValidation";
import type { NormalizedPageRect, PdfRegion, ShowRegionCommand } from "./types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

type PageState = {
  page: PDFPageProxy;
  viewport: PageViewport;
  pageElement: HTMLElement;
  highlightLayer: HTMLElement;
  canvas: HTMLCanvasElement | null;
  textLayer: HTMLElement | null;
  textLayerBuilder: TextLayerBuilder | null;
  renderPromise: Promise<void> | null;
  renderTask: RenderTask | null;
  rendered: boolean;
};

type HighlightEntry = {
  requestId: string;
  pageIndex: number;
  regions: PdfRegion[];
  elements: HTMLElement[];
};

export type SelectionRegionGroup = {
  pageIndex: number;
  regions: NormalizedPageRect[];
};

type ViewerElements = {
  pageHost: HTMLElement;
  scrollContainer: HTMLElement;
};

export class ControlledPdfViewer {
  private readonly elements: ViewerElements;
  private documentId = DEFAULT_DOCUMENT_ID;
  private pdf: PDFDocumentProxy | null = null;
  private fitWidth = 1;
  private renderToken = 0;
  private pageObserver: IntersectionObserver | null = null;
  private releaseScrollListener: (() => void) | null = null;
  private releaseAnimationFrame = 0;
  private readonly pages = new Map<number, PageState>();
  private readonly highlights = new Map<string, HighlightEntry>();
  private readonly onStatus: (message: string, level?: "info" | "error") => void;
  private readonly onRegionSelected: (group: SelectionRegionGroup) => void;

  constructor(
    elements: ViewerElements,
    onStatus: (message: string, level?: "info" | "error") => void,
    onRegionSelected: (group: SelectionRegionGroup) => void = () => undefined,
  ) {
    this.elements = elements;
    this.onStatus = onStatus;
    this.onRegionSelected = onRegionSelected;
  }

  async openDocument(input: {
    documentId?: string;
    url: string;
  }): Promise<void> {
    this.renderToken += 1;
    const token = this.renderToken;
    this.documentId = input.documentId ?? DEFAULT_DOCUMENT_ID;
    this.disconnectPageObserver();
    this.releaseAllPageLayers();
    this.pages.clear();
    this.highlights.clear();
    this.setPlaceholder("Loading PDF...");

    const loadingTask = pdfjsLib.getDocument({ url: input.url });

    this.pdf = await loadingTask.promise;
    if (token !== this.renderToken) return;

    this.fitWidth = this.getVisiblePageWidth();
    this.elements.pageHost.replaceChildren();
    this.onStatus(`Opened ${this.pdf.numPages} page document.`);

    for (let pageNumber = 1; pageNumber <= this.pdf.numPages; pageNumber += 1) {
      if (token !== this.renderToken) return;
      await this.createPageShell(pageNumber, token);
    }

    this.resetHorizontalScroll();
    this.observePages(token);
  }

  async showRegions(command: ShowRegionCommand): Promise<void> {
    const normalized = normalizeShowRegionCommand(command);

    if (normalized.documentId !== this.documentId) {
      throw new Error(
        `Command targets "${normalized.documentId}", but active document is "${this.documentId}".`,
      );
    }

    if (!this.pdf) {
      throw new Error("No PDF is open.");
    }

    const pageState = await this.ensurePageRendered(normalized.pageIndex);

    if (normalized.replace) this.clearHighlight(normalized.requestId);

    const highlightElements = normalized.regions.map((region, index) => {
      const rect = this.toViewportRect(region, pageState.viewport);
      const highlight = document.createElement("div");
      highlight.className = "remote-highlight";
      highlight.dataset.requestId = normalized.requestId;
      highlight.dataset.regionIndex = String(index);
      highlight.style.left = `${rect.left}px`;
      highlight.style.top = `${rect.top}px`;
      highlight.style.width = `${rect.width}px`;
      highlight.style.height = `${rect.height}px`;
      pageState.highlightLayer.append(highlight);
      return highlight;
    });

    this.highlights.set(normalized.requestId, {
      requestId: normalized.requestId,
      pageIndex: normalized.pageIndex,
      regions: normalized.regions,
      elements: highlightElements,
    });

    const firstHighlight = highlightElements[0];
    if (normalized.behavior.scroll !== "none") {
      this.scrollHighlightIntoView(firstHighlight, normalized.behavior.smooth);
    }

    if (normalized.behavior.pulse) {
      for (const highlight of highlightElements) {
        highlight.classList.remove("is-pulsing");
        requestAnimationFrame(() => highlight.classList.add("is-pulsing"));
      }
    }

    if (normalized.behavior.durationMs > 0) {
      window.setTimeout(
        () => this.clearHighlight(normalized.requestId),
        normalized.behavior.durationMs,
      );
    }

    this.onStatus(
      `Displayed ${highlightElements.length} region(s) on page ${normalized.pageIndex + 1}.`,
    );
  }

  clearHighlight(requestId: string): void {
    const entry = this.highlights.get(requestId);
    if (!entry) return;

    for (const element of entry.elements) {
      element.remove();
    }
    this.highlights.delete(requestId);
  }

  clearAllHighlights(announce = true): void {
    for (const requestId of this.highlights.keys()) {
      this.clearHighlight(requestId);
    }
    if (announce) this.onStatus("Cleared highlights.");
  }

  getSelectionRegionGroups(): SelectionRegionGroup[] {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return [];
    }

    const regionsByPage = new Map<number, NormalizedPageRect[]>();

    for (let rangeIndex = 0; rangeIndex < selection.rangeCount; rangeIndex += 1) {
      const range = selection.getRangeAt(rangeIndex);

      for (const clientRect of range.getClientRects()) {
        if (clientRect.width < 1 || clientRect.height < 1) continue;

        for (const [pageIndex, pageState] of this.pages) {
          if (!pageState.textLayer) continue;

          const pageRect = pageState.pageElement.getBoundingClientRect();
          const left = Math.max(clientRect.left, pageRect.left);
          const right = Math.min(clientRect.right, pageRect.right);
          const top = Math.max(clientRect.top, pageRect.top);
          const bottom = Math.min(clientRect.bottom, pageRect.bottom);
          const width = right - left;
          const height = bottom - top;

          if (width < 1 || height < 1) continue;

          const regions = regionsByPage.get(pageIndex) ?? [];
          regions.push({
            coordinateSpace: "normalized-page",
            x: roundRegionValue((left - pageRect.left) / pageRect.width),
            y: roundRegionValue((top - pageRect.top) / pageRect.height),
            width: roundRegionValue(width / pageRect.width),
            height: roundRegionValue(height / pageRect.height),
          });
          regionsByPage.set(pageIndex, regions);
        }
      }
    }

    return [...regionsByPage.entries()]
      .sort(([pageA], [pageB]) => pageA - pageB)
      .map(([pageIndex, regions]) => ({
        pageIndex,
        regions: mergeSelectionRects(regions),
      }))
      .filter((group) => group.regions.length > 0);
  }

  async refitWidth(): Promise<void> {
    if (!this.pdf) return;

    const nextFitWidth = this.getVisiblePageWidth();
    if (Math.abs(nextFitWidth - this.fitWidth) < 2) {
      this.resetHorizontalScroll();
      return;
    }

    this.fitWidth = nextFitWidth;
    await this.rerenderAtCurrentZoom();
  }

  private async rerenderAtCurrentZoom(): Promise<void> {
    if (!this.pdf) return;

    const activeHighlights = [...this.highlights.values()].map((entry) => ({
      type: "pdf.show-region" as const,
      requestId: entry.requestId,
      documentId: this.documentId,
      pageIndex: entry.pageIndex,
      regions: entry.regions,
      behavior: { scroll: "none" as const },
    }));

    this.renderToken += 1;
    const token = this.renderToken;
    this.disconnectPageObserver();
    this.releaseAllPageLayers();
    this.pages.clear();
    this.highlights.clear();
    this.elements.pageHost.replaceChildren();

    for (let pageNumber = 1; pageNumber <= this.pdf.numPages; pageNumber += 1) {
      if (token !== this.renderToken) return;
      await this.createPageShell(pageNumber, token);
    }

    this.resetHorizontalScroll();
    this.observePages(token);

    for (const highlight of activeHighlights) {
      await this.showRegions(highlight);
    }
  }

  private async createPageShell(pageNumber: number, token: number): Promise<void> {
    if (!this.pdf) return;

    const page = await this.pdf.getPage(pageNumber);
    if (token !== this.renderToken) return;

    const viewport = this.getPageFitViewport(page);
    const pageElement = document.createElement("article");
    pageElement.className = "pdf-page";
    pageElement.dataset.pageIndex = String(pageNumber - 1);
    pageElement.style.width = `${Math.floor(viewport.width)}px`;
    pageElement.style.height = `${Math.floor(viewport.height)}px`;
    pageElement.style.setProperty("--total-scale-factor", String(viewport.scale));
    this.enableRegionDragging(pageElement, pageNumber - 1);

    const highlightLayer = document.createElement("div");
    highlightLayer.className = "remote-highlight-layer";

    pageElement.append(highlightLayer);
    this.elements.pageHost.append(pageElement);

    this.pages.set(pageNumber - 1, {
      page,
      viewport,
      pageElement,
      highlightLayer,
      canvas: null,
      textLayer: null,
      textLayerBuilder: null,
      renderPromise: null,
      renderTask: null,
      rendered: false,
    });
  }

  private enableRegionDragging(
    pageElement: HTMLElement,
    pageIndex: number,
  ): void {
    pageElement.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || event.pointerType === "touch") return;
      if ((event.target as Element).closest(".textLayer span")) return;

      event.preventDefault();
      window.getSelection()?.removeAllRanges();

      const pageRect = pageElement.getBoundingClientRect();
      const start = pointWithinPage(event, pageRect);
      const draft = document.createElement("div");
      draft.className = "region-draft";
      pageElement.append(draft);
      pageElement.classList.add("is-dragging-region");
      pageElement.setPointerCapture(event.pointerId);

      const updateDraft = (pointerEvent: PointerEvent) => {
        const current = pointWithinPage(pointerEvent, pageRect);
        const left = Math.min(start.x, current.x);
        const top = Math.min(start.y, current.y);
        const width = Math.abs(current.x - start.x);
        const height = Math.abs(current.y - start.y);
        draft.style.left = `${left}px`;
        draft.style.top = `${top}px`;
        draft.style.width = `${width}px`;
        draft.style.height = `${height}px`;
      };

      const finishDrag = (pointerEvent: PointerEvent, commit: boolean) => {
        updateDraft(pointerEvent);
        const draftRect = draft.getBoundingClientRect();
        draft.remove();
        pageElement.classList.remove("is-dragging-region");
        pageElement.releasePointerCapture(pointerEvent.pointerId);
        pageElement.removeEventListener("pointermove", updateDraft);
        pageElement.removeEventListener("pointerup", onPointerUp);
        pageElement.removeEventListener("pointercancel", onPointerCancel);

        if (!commit || draftRect.width < 4 || draftRect.height < 4) return;

        this.onRegionSelected({
          pageIndex,
          regions: [
            {
              coordinateSpace: "normalized-page",
              x: roundRegionValue((draftRect.left - pageRect.left) / pageRect.width),
              y: roundRegionValue((draftRect.top - pageRect.top) / pageRect.height),
              width: roundRegionValue(draftRect.width / pageRect.width),
              height: roundRegionValue(draftRect.height / pageRect.height),
            },
          ],
        });
      };

      const onPointerUp = (pointerEvent: PointerEvent) => {
        finishDrag(pointerEvent, true);
      };
      const onPointerCancel = (pointerEvent: PointerEvent) => {
        finishDrag(pointerEvent, false);
      };

      pageElement.addEventListener("pointermove", updateDraft);
      pageElement.addEventListener("pointerup", onPointerUp);
      pageElement.addEventListener("pointercancel", onPointerCancel);
      updateDraft(event);
    });
  }

  private observePages(token: number): void {
    this.disconnectPageObserver();

    this.pageObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const pageIndex = Number(
            (entry.target as HTMLElement).dataset.pageIndex,
          );

          if (entry.isIntersecting) {
            this.renderPageContent(pageIndex, token).catch((error: unknown) => {
              this.onStatus(
                error instanceof Error ? error.message : "Could not render page.",
                "error",
              );
            });
          } else {
            this.releasePageLayers(pageIndex);
          }
        }
      },
      {
        root: this.elements.scrollContainer,
        rootMargin: "320px 0px",
        threshold: 0,
      },
    );

    for (const pageState of this.pages.values()) {
      this.pageObserver.observe(pageState.pageElement);
    }

    const onScroll = () => {
      if (this.releaseAnimationFrame) return;

      this.releaseAnimationFrame = window.requestAnimationFrame(() => {
        this.releaseAnimationFrame = 0;
        this.releaseDistantPageCanvases();
      });
    };
    this.elements.scrollContainer.addEventListener("scroll", onScroll, {
      passive: true,
    });
    this.releaseScrollListener = () => {
      this.elements.scrollContainer.removeEventListener("scroll", onScroll);
    };
  }

  private disconnectPageObserver(): void {
    this.pageObserver?.disconnect();
    this.pageObserver = null;
    this.releaseScrollListener?.();
    this.releaseScrollListener = null;
    if (this.releaseAnimationFrame) {
      window.cancelAnimationFrame(this.releaseAnimationFrame);
      this.releaseAnimationFrame = 0;
    }
  }

  private async ensurePageRendered(pageIndex: number): Promise<PageState> {
    const pageState = this.pages.get(pageIndex);
    if (!pageState) {
      throw new Error(`Page index ${pageIndex} is not available.`);
    }

    await this.renderPageContent(pageIndex, this.renderToken);
    return pageState;
  }

  private async renderPageContent(pageIndex: number, token: number): Promise<void> {
    const pageState = this.pages.get(pageIndex);
    if (!pageState || pageState.rendered) return;
    if (pageState.renderPromise) return pageState.renderPromise;

    pageState.renderPromise = this.renderPageLayers(pageState, token);
    await pageState.renderPromise;
  }

  private async renderPageLayers(
    pageState: PageState,
    token: number,
  ): Promise<void> {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not create canvas context.");

    const outputScale = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(pageState.viewport.width * outputScale);
    canvas.height = Math.floor(pageState.viewport.height * outputScale);
    canvas.style.width = `${Math.floor(pageState.viewport.width)}px`;
    canvas.style.height = `${Math.floor(pageState.viewport.height)}px`;

    pageState.pageElement.prepend(canvas);
    pageState.canvas = canvas;

    const renderTask = pageState.page.render({
      canvas,
      canvasContext: context,
      transform:
        outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
      viewport: pageState.viewport,
    });
    pageState.renderTask = renderTask;

    try {
      await renderTask.promise;
      if (token !== this.renderToken) {
        canvas.remove();
        return;
      }

      await this.renderTextLayer(pageState, token);
      if (token !== this.renderToken) {
        this.removeTextLayer(pageState);
        canvas.remove();
        return;
      }

      pageState.rendered = true;
    } finally {
      pageState.renderPromise = null;
      pageState.renderTask = null;
    }
  }

  private async renderTextLayer(
    pageState: PageState,
    token: number,
  ): Promise<void> {
    const textLayerBuilder = new TextLayerBuilder({
      pdfPage: pageState.page,
      onAppend: (textLayer: HTMLElement) => {
        if (token !== this.renderToken) return;

        pageState.textLayer = textLayer;
        pageState.pageElement.insertBefore(textLayer, pageState.highlightLayer);
      },
    });

    pageState.textLayerBuilder = textLayerBuilder;

    await textLayerBuilder.render({
      viewport: pageState.viewport,
      images: null as never,
    });
  }

  private releasePageLayers(pageIndex: number): void {
    const pageState = this.pages.get(pageIndex);
    if (!pageState || !pageState.rendered) return;

    pageState.canvas?.remove();
    pageState.canvas = null;
    this.removeTextLayer(pageState);
    pageState.rendered = false;
  }

  private releaseAllPageLayers(): void {
    for (const pageState of this.pages.values()) {
      pageState.canvas?.remove();
      pageState.canvas = null;
      this.removeTextLayer(pageState);
      pageState.renderTask?.cancel();
      pageState.renderTask = null;
      pageState.renderPromise = null;
      pageState.rendered = false;
    }
  }

  private removeTextLayer(pageState: PageState): void {
    pageState.textLayerBuilder?.cancel();
    pageState.textLayerBuilder = null;
    pageState.textLayer?.remove();
    pageState.textLayer = null;
  }

  private releaseDistantPageCanvases(): void {
    const containerBox = this.elements.scrollContainer.getBoundingClientRect();
    const buffer = 320;

    for (const [pageIndex, pageState] of this.pages) {
      const pageBox = pageState.pageElement.getBoundingClientRect();
      const isFarAbove = pageBox.bottom < containerBox.top - buffer;
      const isFarBelow = pageBox.top > containerBox.bottom + buffer;

      if (isFarAbove || isFarBelow) {
        this.releasePageLayers(pageIndex);
      }
    }
  }

  private scrollHighlightIntoView(
    highlight: HTMLElement,
    smooth: boolean,
  ): void {
    const container = this.elements.scrollContainer;
    const containerBox = container.getBoundingClientRect();
    const highlightBox = highlight.getBoundingClientRect();
    const highlightTop =
      highlightBox.top - containerBox.top + container.scrollTop;
    const centeredTop =
      highlightTop - container.clientHeight / 2 + highlightBox.height / 2;

    container.scrollTo({
      top: Math.max(0, centeredTop),
      left: 0,
      behavior: smooth ? "smooth" : "auto",
    });
    this.resetHorizontalScroll();
  }

  private getVisiblePageWidth(): number {
    const container = this.elements.scrollContainer;
    const styles = window.getComputedStyle(container);
    const horizontalPadding =
      Number.parseFloat(styles.paddingLeft) + Number.parseFloat(styles.paddingRight);
    const fitSafetyGap = 24;

    return Math.max(1, container.clientWidth - horizontalPadding - fitSafetyGap);
  }

  private getPageFitViewport(page: PDFPageProxy): PageViewport {
    const unscaledViewport = page.getViewport({ scale: 1 });
    const scale = Math.floor((this.fitWidth / unscaledViewport.width) * 1000) / 1000;

    return page.getViewport({ scale: Math.min(3, Math.max(0.1, scale)) });
  }

  private resetHorizontalScroll(): void {
    this.elements.scrollContainer.scrollLeft = 0;
  }

  private setPlaceholder(message: string): void {
    this.elements.pageHost.replaceChildren();
    const placeholder = document.createElement("div");
    placeholder.className = "page-placeholder";
    placeholder.textContent = message;
    this.elements.pageHost.append(placeholder);
  }

  private toViewportRect(region: PdfRegion, viewport: PageViewport) {
    if (region.coordinateSpace === "normalized-page") {
      return {
        left: region.x * viewport.width,
        top: region.y * viewport.height,
        width: region.width * viewport.width,
        height: region.height * viewport.height,
      };
    }

    const [leftA, topA] = viewport.convertToViewportPoint(region.x1, region.y1);
    const [rightA, bottomA] = viewport.convertToViewportPoint(
      region.x2,
      region.y2,
    );
    const left = Math.min(leftA, rightA);
    const top = Math.min(topA, bottomA);

    return {
      left,
      top,
      width: Math.abs(rightA - leftA),
      height: Math.abs(bottomA - topA),
    };
  }
}

function roundRegionValue(value: number): number {
  return Math.round(Math.min(1, Math.max(0, value)) * 10_000) / 10_000;
}

function pointWithinPage(
  event: PointerEvent,
  pageRect: DOMRect,
): { x: number; y: number } {
  return {
    x: Math.min(pageRect.width, Math.max(0, event.clientX - pageRect.left)),
    y: Math.min(pageRect.height, Math.max(0, event.clientY - pageRect.top)),
  };
}

function mergeSelectionRects(regions: NormalizedPageRect[]): NormalizedPageRect[] {
  const sorted = [...regions].sort((a, b) => a.y - b.y || a.x - b.x);
  const merged: NormalizedPageRect[] = [];
  const lineTolerance = 0.006;

  for (const region of sorted) {
    const previous = merged.at(-1);
    if (!previous) {
      merged.push(region);
      continue;
    }

    const sameLine =
      Math.abs(previous.y - region.y) <= lineTolerance &&
      Math.abs(previous.height - region.height) <= lineTolerance;
    const touchesOrOverlaps = region.x <= previous.x + previous.width + 0.012;

    if (!sameLine || !touchesOrOverlaps) {
      merged.push(region);
      continue;
    }

    const left = Math.min(previous.x, region.x);
    const top = Math.min(previous.y, region.y);
    const right = Math.max(previous.x + previous.width, region.x + region.width);
    const bottom = Math.max(
      previous.y + previous.height,
      region.y + region.height,
    );

    previous.x = roundRegionValue(left);
    previous.y = roundRegionValue(top);
    previous.width = roundRegionValue(right - left);
    previous.height = roundRegionValue(bottom - top);
  }

  return merged;
}
